import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SchemaOrg from '../../components/feature/SchemaOrg';

export default function CalculatorPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    moveType: 'studio', // studio, family, office
    area: '10',
    fromFloor: '1',
    toFloor: '1',
    fromElevator: 'yes',
    toElevator: 'yes',
    distance: 'within10', // within10, 10to30, 30to50, over50
    packingService: 'no',
    furnitureAssembly: 'no',
    pianoMoving: 'no',
    wasteDisposal: 'no'
  });

  const [estimatedPrice, setEstimatedPrice] = useState({ min: 0, max: 0 });

  // 計算價格
  const calculatePrice = () => {
    let basePrice = 0;
    let maxPrice = 0;

    // 基礎價格（依搬家類型）
    switch (formData.moveType) {
      case 'studio':
        basePrice = 3000;
        maxPrice = 5000;
        break;
      case 'family':
        if (parseInt(formData.area) <= 20) {
          basePrice = 5000;
          maxPrice = 8000;
        } else if (parseInt(formData.area) <= 35) {
          basePrice = 8000;
          maxPrice = 12000;
        } else {
          basePrice = 12000;
          maxPrice = 18000;
        }
        break;
      case 'office':
        basePrice = 8000;
        maxPrice = 20000;
        break;
    }

    // 樓層費用（無電梯）
    if (formData.fromElevator === 'no') {
      const fromFloor = parseInt(formData.fromFloor);
      if (fromFloor > 1) {
        const floorFee = (fromFloor - 1) * 500;
        basePrice += floorFee;
        maxPrice += floorFee;
      }
    }

    if (formData.toElevator === 'no') {
      const toFloor = parseInt(formData.toFloor);
      if (toFloor > 1) {
        const floorFee = (toFloor - 1) * 500;
        basePrice += floorFee;
        maxPrice += floorFee;
      }
    }

    // 距離費用
    switch (formData.distance) {
      case '10to30':
        basePrice += 1000;
        maxPrice += 1500;
        break;
      case '30to50':
        basePrice += 2000;
        maxPrice += 3000;
        break;
      case 'over50':
        basePrice += 3000;
        maxPrice += 5000;
        break;
    }

    // 額外服務
    if (formData.packingService === 'yes') {
      basePrice += 2000;
      maxPrice += 4000;
    }

    if (formData.furnitureAssembly === 'yes') {
      basePrice += 1000;
      maxPrice += 2000;
    }

    if (formData.pianoMoving === 'yes') {
      basePrice += 3500;
      maxPrice += 6000;
    }

    if (formData.wasteDisposal === 'yes') {
      basePrice += 3000;
      maxPrice += 7000;
    }

    setEstimatedPrice({ min: basePrice, max: maxPrice });
  };

  useEffect(() => {
    calculatePrice();
  }, [formData]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      <SchemaOrg type="website" />

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            <strong>搬家費用計算器</strong>
          </h1>
          <p className="text-lg md:text-xl text-teal-100 mb-6">
            輸入搬家資訊，立即獲得<strong className="text-white">透明價格估算</strong>
          </p>
          <div className="flex items-center justify-center gap-2 text-white/90">
            <i className="ri-shield-check-line text-2xl"></i>
            <span>價格透明 · 絕無隱藏費用 · 免費估價</span>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* 左側：表單 */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                    <i className="ri-calculator-line text-teal-600 text-xl"></i>
                  </div>
                  填寫搬家資訊
                </h2>

                <div className="space-y-6">
                  {/* 搬家類型 */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3">
                      <i className="ri-home-line mr-2 text-teal-600"></i>
                      搬家類型
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'studio', label: '套房搬遷', icon: 'ri-home-4-line' },
                        { value: 'family', label: '家庭搬遷', icon: 'ri-home-heart-line' },
                        { value: 'office', label: '辦公室搬遷', icon: 'ri-building-line' }
                      ].map(type => (
                        <button
                          key={type.value}
                          onClick={() => handleChange('moveType', type.value)}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            formData.moveType === type.value
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : 'border-gray-200 hover:border-teal-300'
                          }`}
                        >
                          <i className={`${type.icon} text-2xl mb-2`}></i>
                          <div className="font-semibold text-sm">{type.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 坪數 */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3">
                      <i className="ri-ruler-line mr-2 text-teal-600"></i>
                      坪數大小
                    </label>
                    <input
                      type="number"
                      value={formData.area}
                      onChange={(e) => handleChange('area', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none"
                      placeholder="請輸入坪數"
                      min="1"
                    />
                  </div>

                  {/* 起點樓層 */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-3">
                        <i className="ri-arrow-up-line mr-2 text-teal-600"></i>
                        起點樓層
                      </label>
                      <input
                        type="number"
                        value={formData.fromFloor}
                        onChange={(e) => handleChange('fromFloor', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-3">
                        <i className="ri-arrow-down-line mr-2 text-teal-600"></i>
                        終點樓層
                      </label>
                      <input
                        type="number"
                        value={formData.toFloor}
                        onChange={(e) => handleChange('toFloor', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none"
                        min="1"
                      />
                    </div>
                  </div>

                  {/* 電梯 */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-3">起點有電梯？</label>
                      <div className="flex gap-3">
                        {[
                          { value: 'yes', label: '有電梯' },
                          { value: 'no', label: '無電梯' }
                        ].map(option => (
                          <button
                            key={option.value}
                            onClick={() => handleChange('fromElevator', option.value)}
                            className={`flex-1 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                              formData.fromElevator === option.value
                                ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                                : 'border-gray-200 hover:border-teal-300'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-3">終點有電梯？</label>
                      <div className="flex gap-3">
                        {[
                          { value: 'yes', label: '有電梯' },
                          { value: 'no', label: '無電梯' }
                        ].map(option => (
                          <button
                            key={option.value}
                            onClick={() => handleChange('toElevator', option.value)}
                            className={`flex-1 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                              formData.toElevator === option.value
                                ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                                : 'border-gray-200 hover:border-teal-300'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 距離 */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3">
                      <i className="ri-map-pin-distance-line mr-2 text-teal-600"></i>
                      搬家距離
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: 'within10', label: '10公里內' },
                        { value: '10to30', label: '10-30公里' },
                        { value: '30to50', label: '30-50公里' },
                        { value: 'over50', label: '50公里以上' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => handleChange('distance', option.value)}
                          className={`py-3 px-2 rounded-xl border-2 transition-all cursor-pointer text-sm ${
                            formData.distance === option.value
                              ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                              : 'border-gray-200 hover:border-teal-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 額外服務 */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3">
                      <i className="ri-service-line mr-2 text-teal-600"></i>
                      額外服務（可複選）
                    </label>
                    <div className="space-y-3">
                      {[
                        { field: 'packingService', label: '專業打包服務', icon: 'ri-box-3-line' },
                        { field: 'furnitureAssembly', label: '家具拆裝服務', icon: 'ri-tools-line' },
                        { field: 'pianoMoving', label: '鋼琴搬運', icon: 'ri-music-line' },
                        { field: 'wasteDisposal', label: '廢棄物清運', icon: 'ri-delete-bin-line' }
                      ].map(service => (
                        <label
                          key={service.field}
                          className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-teal-300 transition-all cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData[service.field as keyof typeof formData] === 'yes'}
                            onChange={(e) => handleChange(service.field, e.target.checked ? 'yes' : 'no')}
                            className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <i className={`${service.icon} text-xl text-teal-600`}></i>
                          <span className="font-medium text-gray-700">{service.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 右側：價格顯示 */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl shadow-xl p-6 md:p-8 text-white sticky top-4">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <i className="ri-price-tag-3-line text-3xl"></i>
                  預估費用
                </h3>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <div className="text-center">
                    <div className="text-sm text-teal-100 mb-2">預估價格區間</div>
                    <div className="text-4xl md:text-5xl font-bold mb-2">
                      ${estimatedPrice.min.toLocaleString()}
                    </div>
                    <div className="text-2xl text-teal-100 mb-2">~</div>
                    <div className="text-4xl md:text-5xl font-bold">
                      ${estimatedPrice.max.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-teal-200 mt-0.5"></i>
                    <span className="text-teal-50">以上為參考價格，實際費用依現場評估為準</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-shield-check-line text-teal-200 mt-0.5"></i>
                    <span className="text-teal-50">透明收費，絕無隱藏費用</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-customer-service-line text-teal-200 mt-0.5"></i>
                    <span className="text-teal-50">提供 24 小時免費專業估價</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <a
                    href="https://line.me/ti/p/@285yavdh"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-white text-teal-600 px-6 py-4 rounded-xl font-bold hover:bg-gray-50 transition-all cursor-pointer shadow-lg hover:shadow-2xl whitespace-nowrap"
                  >
                    <img 
                      src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                      alt="LINE" 
                      className="w-6 h-6"
                      loading="lazy"
                    />
                    <span>LINE 免費估價</span>
                  </a>
                  <a
                    href="tel:0978900081"
                    className="flex items-center justify-center gap-2 w-full bg-teal-700 text-white px-6 py-4 rounded-xl font-bold hover:bg-teal-800 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    <span>立即來電諮詢</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 價格說明 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">搬家費用說明</h2>
            <p className="text-lg text-gray-600">了解各項費用計算方式</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">基礎搬運費</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• 套房：$3,000-5,000</li>
                <li>• 兩房：$5,000-8,000</li>
                <li>• 三房：$8,000-12,000</li>
                <li>• 四房以上：$12,000起</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <i className="ri-stairs-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">樓層費用</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• 有電梯：免收樓層費</li>
                <li>• 無電梯：每層 $500</li>
                <li>• 2樓：+$500</li>
                <li>• 5樓：+$2,000</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-distance-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">距離費用</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• 10公里內：免收</li>
                <li>• 10-30公里：+$1,000</li>
                <li>• 30-50公里：+$2,000</li>
                <li>• 50公里以上：另議</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <i className="ri-service-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">額外服務</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• 打包服務：+$2,000起</li>
                <li>• 家具拆裝：+$1,000起</li>
                <li>• 鋼琴搬運：+$3,500起</li>
                <li>• 廢棄物清運：+$3,000起</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">需要更精確的報價？</h2>
          <p className="text-lg text-gray-600 mb-8">
            聯絡我們，專業顧問將為您提供免費現場估價服務
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://line.me/ti/p/@285yavdh"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-green-700 transition-all cursor-pointer shadow-lg hover:shadow-2xl whitespace-nowrap"
            >
              <img 
                src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                alt="LINE" 
                className="w-6 h-6"
                loading="lazy"
              />
              <span>LINE 預約估價</span>
            </a>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-teal-700 transition-all cursor-pointer shadow-lg hover:shadow-2xl whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line text-xl"></i>
              <span>查看完整價目表</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
